import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  StarIcon,
  ChartBarIcon,
  CogIcon,
  SparklesIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
  BoltIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Footer from '../components/Footer';
import PaymentForm from '../components/PaymentForm';

function FeatureCard({ icon: Icon, title, description, benefits = [] }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="card p-6 flex flex-col h-full"
    >
      <div className="flex items-center mb-4">
        <div className="bg-gray-custom-800 p-3 rounded-lg">
          <Icon className="h-8 w-8 text-gray-custom-300" />
        </div>
        <h3 className="text-xl font-semibold ml-3 text-gray-custom-100">{title}</h3>
      </div>
      <p className="text-gray-custom-400 mb-4">{description}</p>

      {benefits.length > 0 && (
        <ul className="mt-auto space-y-2">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm text-gray-custom-300">{benefit}</span>
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  );
}

function FaqItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-custom-700 py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full text-left"
      >
        <h4 className="text-lg font-medium text-gray-custom-100 flex items-center">
          <QuestionMarkCircleIcon className="h-5 w-5 text-gray-custom-400 mr-2" />
          {question}
        </h4>
        <ArrowRightIcon
          className={`h-5 w-5 text-gray-custom-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="mt-2 pl-7 pr-2 text-gray-custom-400">
          {answer}
        </div>
      )}
    </div>
  );
}

function Premium() {
  const features = [
    {
      icon: ShieldCheckIcon,
      title: "Advanced Moderation",
      description: "Access to advanced moderation tools to keep your server safe and well-managed.",
      benefits: [
        "Ban users by ID without them being in the server",
        "Advanced auto-moderation with custom rules",
        "Detailed audit logs with extended history",
        "Temporary bans with automatic unbanning"
      ]
    },
    {
      icon: StarIcon,
      title: "Priority Support",
      description: "Get priority access to our support team and faster response times.",
      benefits: [
        "Dedicated support channel in our Discord",
        "Priority ticket handling",
        "Direct contact with developers",
        "Early access to new features"
      ]
    },
    {
      icon: ChartBarIcon,
      title: "Analytics Dashboard",
      description: "Detailed server analytics and insights to help grow your community.",
      benefits: [
        "Member activity tracking and reports",
        "Command usage statistics",
        "Growth trends and projections",
        "Engagement metrics and recommendations"
      ]
    },
    {
      icon: WrenchScrewdriverIcon,
      title: "Custom Commands",
      description: "Create and manage custom commands with advanced parameters.",
      benefits: [
        "Create up to 50 custom commands (vs 10 for free)",
        "Advanced command parameters and conditions",
        "Custom command cooldowns and permissions",
        "Command aliases and shortcuts"
      ]
    },
    {
      icon: CurrencyDollarIcon,
      title: "Enhanced Economy",
      description: "Advanced economy features for a more engaging server experience.",
      benefits: [
        "Custom shops with unlimited items",
        "Auction system for member trading",
        "Advanced item crafting and recipes",
        "Economy leaderboards and statistics"
      ]
    },
    {
      icon: CogIcon,
      title: "Advanced Configuration",
      description: "Fine-tune every aspect of Overseer with advanced options.",
      benefits: [
        "Per-channel and per-role settings",
        "Custom welcome and leave messages",
        "Advanced auto-role configuration",
        "Custom prefix and command permissions"
      ]
    }
  ];

  const faqs = [
    {
      question: "Is Premium really a one-time payment?",
      answer: (
        <p>
          Yes! Overseer Premium is a one-time payment of £1 per server for lifetime access.
          There are no recurring charges or hidden fees. Once you purchase Premium for a server,
          it remains Premium forever.
        </p>
      )
    },
    {
      question: "How do I activate Premium after purchase?",
      answer: (
        <p>
          After completing your purchase, you'll receive a confirmation with your server ID.
          Simply use the <code className="bg-gray-custom-800 px-1 py-0.5 rounded text-gray-custom-200">!premium activate</code> command
          in your server, and all Premium features will be instantly available.
        </p>
      )
    },
    {
      question: "Can I transfer Premium to another server?",
      answer: (
        <p>
          Premium is tied to a specific server ID and cannot be transferred. However, at just £1 per server,
          it's affordable to purchase Premium for multiple servers if needed.
        </p>
      )
    },
    {
      question: "What happens if I already have a Premium code?",
      answer: (
        <p>
          If you have a Premium code from being a beta tester, contributor, or developer,
          you can redeem it using the <code className="bg-gray-custom-800 px-1 py-0.5 rounded text-gray-custom-200">!premium redeem [code]</code> command
          in your server. These codes provide the same lifetime Premium access.
        </p>
      )
    },
    {
      question: "Do you offer refunds?",
      answer: (
        <p>
          Since Premium is a digital product that is activated immediately, we generally don't offer refunds.
          However, if you encounter any issues, please contact our support team, and we'll do our best to help.
        </p>
      )
    }
  ];

  return (
    <div>
      <div className="container-custom py-24">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-gray-custom-800/80 to-gray-custom-900/80 border border-gray-custom-700 rounded-xl p-8 mb-12 shadow-lg">
              <div className="bg-gray-custom-800 rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <SparklesIcon className="h-8 w-8 text-gray-custom-300" />
              </div>

              <h1 className="text-3xl font-bold text-gray-custom-100 mb-4">
                Overseer Premium
              </h1>

              <p className="text-xl text-gray-custom-300 mb-6">
                Unlock advanced features for just <span className="font-bold text-white">£1 lifetime</span> per server
              </p>

              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <div className="flex items-center bg-gray-custom-800/50 px-4 py-2 rounded-full">
                  <BoltIcon className="h-5 w-5 text-gray-custom-400 mr-2" />
                  <span className="text-gray-custom-300 text-sm">One-time payment</span>
                </div>
                <div className="flex items-center bg-gray-custom-800/50 px-4 py-2 rounded-full">
                  <ClockIcon className="h-5 w-5 text-gray-custom-400 mr-2" />
                  <span className="text-gray-custom-300 text-sm">Lifetime access</span>
                </div>
                <div className="flex items-center bg-gray-custom-800/50 px-4 py-2 rounded-full">
                  <CheckCircleIcon className="h-5 w-5 text-gray-custom-400 mr-2" />
                  <span className="text-gray-custom-300 text-sm">Instant activation</span>
                </div>
              </div>

              <div className="bg-gray-custom-800/50 border border-gray-custom-700 rounded-lg p-4 text-left">
                <h2 className="text-lg font-semibold text-gray-custom-100 mb-2">
                  Important: Premium is Optional
                </h2>
                <p className="text-gray-custom-400 text-sm mb-2">
                  Overseer is fully functional without Premium. All core commands and features are available to everyone.
                  Premium simply adds quality-of-life improvements and advanced options to enhance your experience.
                </p>
                <p className="text-gray-custom-400 text-sm">
                  That's why we've kept Premium at just £1 lifetime per server. Beta servers, contributors, and developers
                  receive free lifetime codes (not user/server locked - feel free to share them!).
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-custom-100 mb-4">Premium Features</h2>
            <p className="text-xl text-gray-custom-400 max-w-3xl mx-auto">
              Enhance your server management with these premium features. Remember, these are optional improvements to an already powerful bot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <FeatureCard {...feature} />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Payment Section */}
        <div className="mb-20">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="md:col-span-2">
                <div className="sticky top-8">
                  <div className="bg-gray-custom-800/50 border border-gray-custom-700 rounded-xl p-6 mb-6">
                    <h3 className="text-2xl font-bold text-gray-custom-100 mb-4">
                      Upgrade to Premium
                    </h3>
                    <div className="flex items-center mb-6">
                      <div className="text-3xl font-bold text-white">£1</div>
                      <div className="text-gray-custom-400 ml-2">
                        <span className="block text-sm">one-time payment</span>
                        <span className="block text-xs">lifetime access</span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-custom-300">All premium features</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-custom-300">Lifetime access</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-custom-300">Priority support</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-custom-300">Early access to new features</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="md:col-span-3">
                <div className="bg-gray-custom-800/50 border border-gray-custom-700 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-gray-custom-100 mb-6">
                    Payment Details
                  </h3>
                  <PaymentForm />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-custom-100 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-custom-400">
              Have questions about Premium? Find answers to common questions below.
            </p>
          </div>

          <div className="bg-gray-custom-800/30 border border-gray-custom-700 rounded-xl p-6">
            {faqs.map((faq, index) => (
              <FaqItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Premium;